import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import type { FC } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { MuiFormSelect } from 'components/Form';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';
import { formatInputAmount } from 'helpers/locale';

import { getPopupContentStyle } from './AddToWipPopup.styled';
import useAddToWipPopup from './hooks/useAddToWipPopup';
import { type AddToWipFormValuesT, addToWipValidationSchema } from './validationSchema';

type AddToWipPopupPropsT = {
  amount: number;
  isOpen: boolean;
  onClose: NoopT;
  onAddToWipSuccess?: () => void;
  repairOrderNumber: string;
  roofTopId: string;
};

const workItemOptions: MuiFormSelectOptionT[] = [
  { value: 'currentWork', label: 'Current Work' },
  { value: 'backlog', label: 'Backlog' },
];

const AddToWipPopup: FC<AddToWipPopupPropsT> = ({
  amount,
  isOpen,
  onClose,
  onAddToWipSuccess,
  repairOrderNumber,
  roofTopId,
}): JSX.Element => {
  const { isMobile } = useResponsive();
  const { isSubmitting, handleAddToWip, handleClosePopup } = useAddToWipPopup({
    amount,
    onAddToWipSuccess,
    repairOrderNumber,
    roofTopId,
  });

  const formMethods = useForm<AddToWipFormValuesT>({
    resolver: zodResolver(addToWipValidationSchema),
    defaultValues: {
      workItemType: 'currentWork',
    },
    mode: 'onChange',
  });

  const { formState, handleSubmit, reset } = formMethods;

  useEffect((): void => {
    if (isOpen) {
      reset({ workItemType: 'currentWork' });
    }
  }, [isOpen, reset]);

  return (
    <Popup
      nested
      showCloseBtn
      modalTitle="Add to WIP"
      size={ModalSizeE.ADD_TO_WIP}
      popupId={ScrollLockIdE.AddToWipPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isOpen}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      data-testid="add-to-wip-popup"
      validationErrorCount={Object.keys(formState.errors).length}
    >
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ width: '100%' }}>
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={handleSubmit((data): void => {
              handleAddToWip(data, onClose);
            })}
            sx={{
              width: isMobile ? '100%' : 300,
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: isMobile ? undefined : 200,
            }}
          >
            <Box sx={{ mb: 2, width: '100%' }}>
              <TextField
                fullWidth
                size="small"
                label="RO Number"
                value={repairOrderNumber || '—'}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ mb: 2, width: '100%' }}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                value={formatInputAmount(amount)}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ mb: 2, width: '100%' }}>
              <MuiFormSelect
                name="workItemType"
                label="Classification"
                placeholder=""
                options={workItemOptions}
                required
              />
            </Box>
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              gap={1}
              justifyContent={isMobile ? 'stretch' : 'flex-end'}
              sx={{ width: '100%' }}
            >
              <Button
                disabled={isSubmitting}
                sx={{ flex: isMobile ? 1 : undefined }}
                type="submit"
                variant="contained"
              >
                {isSubmitting ? 'Adding...' : 'Add to WIP'}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </Box>
    </Popup>
  );
};

export default AddToWipPopup;
